import {
  PlayCircle as PlayCircleIcon,
  Schedule as ScheduleIcon,
  Timer as TimerIcon,
} from "@mui/icons-material";
import { Box, Card, CardContent, Chip, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { BarChart, PieChart } from "@mui/x-charts";
import type { GridColDef } from "@mui/x-data-grid";
import { Link } from "react-router";
import { DataGrid } from "../../../components/data-grid";
import { theme } from "../../../theme/theme";
import type { SuiteRunMetrics } from "../../../types";
import { formatDuration } from "../../../utils";

interface SuiteRunsTabProps {
  data?: SuiteRunMetrics;
  isLoading?: boolean;
}

const statusColors: Record<string, string> = {
  passed: theme.palette.success.main,
  failed: theme.palette.error.main,
  failed_pending: theme.palette.error.main,
  pending: theme.palette.warning.main,
  running: theme.palette.info.main,
  skipped: theme.palette.grey[500],
  error: theme.palette.error.dark,
  flaky: theme.palette.warning.dark,
  cancelled: theme.palette.grey[600],
  timed_out: theme.palette.error.light,
  no_credits: theme.palette.error.light,
};

const triggerColors: Record<string, string> = {
  manual: theme.palette.primary.main,
  cron: theme.palette.info.main,
  webhook: theme.palette.warning.main,
  action: theme.palette.secondary.main,
};

const columns: GridColDef[] = [
  {
    field: "suite_name",
    headerName: "Suite Name",
    flex: 2,
    minWidth: 200,
    renderCell: (params) => (
      <Link
        to={`/tests-main/test-suites/${params.row.suite_id}`}
        style={{ color: theme.palette.primary.main, textDecoration: "none" }}
      >
        {params.value}
      </Link>
    ),
  },
  {
    field: "total_runs",
    headerName: "Runs",
    width: 80,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "passed",
    headerName: "Passed",
    width: 80,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => (
      <Typography
        color="success.main"
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {params.value}
      </Typography>
    ),
  },
  {
    field: "failed",
    headerName: "Failed",
    width: 80,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => (
      <Typography
        color="error.main"
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {params.value}
      </Typography>
    ),
  },
  {
    field: "pass_rate",
    headerName: "Pass Rate",
    width: 100,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => {
      const rate = params.value as number;
      const color = rate >= 90 ? "success" : rate >= 70 ? "warning" : ("error" as const);
      return <Chip label={`${rate.toFixed(0)}%`} size="small" color={color} />;
    },
  },
  {
    field: "avg_duration_seconds",
    headerName: "Avg Duration",
    width: 120,
    align: "center",
    headerAlign: "center",
    valueFormatter: (value: number) => formatDuration(value),
  },
  {
    field: "last_run_at",
    headerName: "Last Run",
    width: 150,
    valueFormatter: (value) => (value ? new Date(value).toLocaleDateString() : "Never"),
  },
];

export function SuiteRunsTab({ data, isLoading }: SuiteRunsTabProps) {
  const runsByStatus = data?.runs_by_status ?? {};
  const runsByTrigger = data?.runs_by_trigger ?? {};
  const runsBySuite = data?.runs_by_suite ?? [];
  const longestSuites = data?.longest_suites ?? [];

  const totalStatusRuns = Object.values(runsByStatus).reduce((sum, count) => sum + count, 0);
  const statusData = Object.entries(runsByStatus)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([status, count], index) => {
      const percentage = totalStatusRuns > 0 ? ((count / totalStatusRuns) * 100).toFixed(0) : 0;
      return {
        id: index,
        value: count,
        label: `${status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ")} (${percentage}%)`,
        color: statusColors[status] || theme.palette.grey[500],
      };
    });

  const totalTriggerRuns = Object.values(runsByTrigger).reduce((sum, count) => sum + count, 0);
  const triggerData = Object.entries(runsByTrigger)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([trigger, count], index) => {
      const percentage = totalTriggerRuns > 0 ? ((count / totalTriggerRuns) * 100).toFixed(0) : 0;
      return {
        id: index,
        value: count,
        label: `${trigger.charAt(0).toUpperCase() + trigger.slice(1)} (${percentage}%)`,
        color: triggerColors[trigger] || theme.palette.grey[500],
      };
    });

  return (
    <Stack spacing={3}>
      {/* Charts Row */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: 380 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <PlayCircleIcon color="primary" fontSize="small" />
                <Typography variant="h6">Status Distribution</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Suite runs by execution status
              </Typography>
              {isLoading ? (
                <Skeleton variant="rectangular" height={260} sx={{ mt: 2 }} />
              ) : statusData.length > 0 ? (
                <PieChart
                  series={[{ data: statusData, innerRadius: 40 }]}
                  height={260}
                  margin={{ top: 10, bottom: 10, left: 0, right: 0 }}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 260,
                  }}
                >
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: 380 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <ScheduleIcon color="info" fontSize="small" />
                <Typography variant="h6">Trigger Distribution</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                How suite runs are triggered
              </Typography>
              {isLoading ? (
                <Skeleton variant="rectangular" height={260} sx={{ mt: 2 }} />
              ) : triggerData.length > 0 ? (
                <PieChart
                  series={[{ data: triggerData, innerRadius: 40 }]}
                  height={260}
                  margin={{ top: 10, bottom: 10, left: 0, right: 0 }}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 260,
                  }}
                >
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: 380 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <TimerIcon color="warning" fontSize="small" />
                <Typography variant="h6">Longest Suites</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Top 10 suites by avg execution time
              </Typography>
              {isLoading ? (
                <Skeleton variant="rectangular" height={260} sx={{ mt: 2 }} />
              ) : longestSuites.length > 0 ? (
                <BarChart
                  layout="horizontal"
                  series={[
                    {
                      data: longestSuites.slice(0, 5).map((s) => s.avg_duration_seconds),
                      color: theme.palette.warning.main,
                      label: "Avg Duration",
                      valueFormatter: (value) => (value !== null ? formatDuration(value) : ""),
                    },
                  ]}
                  xAxis={[
                    {
                      valueFormatter: (value) => formatDuration(value as number),
                    },
                  ]}
                  yAxis={[
                    {
                      data: longestSuites.slice(0, 5).map((s) => s.suite_name),
                      scaleType: "band",
                    },
                  ]}
                  height={260}
                  margin={{ left: 10 }}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 260,
                  }}
                >
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Suites Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Suite Run Summary
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Per-suite execution statistics for the selected period
          </Typography>
          <DataGrid
            rows={runsBySuite.map((r, idx) => ({
              ...r,
              id: r.suite_id || idx,
            }))}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              sorting: { sortModel: [{ field: "total_runs", sort: "desc" }] },
            }}
            minHeight="500px"
            maxHeight="800px"
          />
        </CardContent>
      </Card>
    </Stack>
  );
}
